/**
 * Normalizador wire → *Local para `GET /me/profile`.
 *
 * El grueso del shape de `/me/profile` ya coincide con `MeProfileResponseLocal` (el front lo consume
 * por passthrough). La única transformación es `externalScopeLevels` (DEC-RBAC-027 / TD-WEBAPP-022):
 * lo materializa SIEMPRE como `Scope[]` (default `[]` si el backend no lo manda) y descarta cualquier
 * valor fuera de `KNOWN_SCOPES` con el MISMO criterio que el resto de los normalizers (`normalizeScope`),
 * para que la sesión nunca hidrate un scope inventado.
 *
 * Se aplica dentro de `meApi.*` (api/endpoints/me.ts).
 */
import type { MeProfileResponseLocal } from './MeLocal';
import type { TenantLevelLocal } from './PlatformLocal';
import type { Scope } from './permissions';
import { KNOWN_SCOPES, normalizeScope } from './tenantNormalizers';

/**
 * Wire de `/me/profile`: el shape *Local con `externalScopeLevels` y `levels` opcionales (pueden no
 * venir todavía — backend anterior a cada feature).
 */
type MeProfileWire = Omit<MeProfileResponseLocal, 'externalScopeLevels' | 'levels'> & {
    externalScopeLevels?: unknown;
    levels?: unknown;
};

/** `externalScopeLevels` del wire → `Scope[]` filtrado por `KNOWN_SCOPES` (default `[]`). */
function normalizeExternalScopeLevels(raw: unknown): Scope[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((v) => normalizeScope(typeof v === 'string' ? v : undefined))
        .filter((s): s is Scope => s !== undefined);
}

/** `levels` del wire → `TenantLevelLocal[]`; descarta niveles desconocidos o sin `displayName`. */
function normalizeLevels(raw: unknown): TenantLevelLocal[] {
    if (!Array.isArray(raw)) return [];
    return (raw as unknown[]).flatMap((item) => {
        const entry = item as { level?: unknown; displayName?: unknown } | null;
        const level = normalizeScope(typeof entry?.level === 'string' ? entry.level : undefined);
        const displayName = typeof entry?.displayName === 'string' ? entry.displayName : undefined;
        if (!level || !displayName) return [];
        return [{ level, displayName } as TenantLevelLocal];
    });
}

export function normalizeMeProfile(wire: MeProfileWire): MeProfileResponseLocal {
    return {
        ...wire,
        externalScopeLevels: normalizeExternalScopeLevels(wire.externalScopeLevels),
        levels: normalizeLevels(wire.levels),
    };
}

// Reexport para tests/consumidores que quieran el criterio de niveles válidos sin importar el normalizer.
export { KNOWN_SCOPES };
